import { parsePlanField, qualifyFieldPath } from "../../utilities/fieldPath";

import { createReviewMenuItems } from "./reviewMenuItems";

import type { ReviewLike, ReviewMenuTarget } from "./reviewMenuItems";
import type {
  CellRange,
  Column,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  IRowNode,
  MenuItemDef,
} from "ag-grid-community";

/**
 * Callbacks provided from PlanEditor (React-land) for field comment CRUD.
 * These wrap the FieldMetadataContext/CellReviewContext methods and dialog interactions.
 *
 * Method shorthand (not property syntax) enables bivariant parameter checking so
 * both BTX (FieldCommentResponse) and MPX (CellReview) callback implementations
 * satisfy this interface without explicit widening.
 */
export interface CellContextMenuCallbacks {
  onNeedsReview(
    planId: string,
    fieldPath: string,
    existingReview?: ReviewLike,
  ): void;
  onClearReview(planId: string, existingReview: ReviewLike): void;
  /** Lookup function — returns a ReviewLike record if a review exists. */
  getFieldComment(planId: string, fieldPath: string): ReviewLike | undefined;
}

/** The cell a context menu is being built for, resolved from the grid params. */
export type MenuCellTarget = ReviewMenuTarget;

/**
 * Builds surface-specific items for the resolved cells: the clicked cell alone,
 * or every cell of the selected ranges when the click lands inside one. Core
 * appends whatever this returns after its own review items, so a surface adds
 * actions without this builder needing to know what they are.
 */
export type ExtraMenuItemsBuilder = (
  targets: MenuCellTarget[],
) => (DefaultMenuItem | MenuItemDef)[];

export const isBenefitLabelColumn = (field: string): boolean =>
  field === "benefit.label";

const resolveCellTarget = (
  column: Column | null | undefined,
  node: IRowNode | null | undefined,
): MenuCellTarget | undefined => {
  const field = column?.getColDef()?.field;
  const benefitKey = node?.data?.benefitKey as string | undefined;

  if (!field || !benefitKey || isBenefitLabelColumn(field)) {
    return undefined;
  }

  const parsed = parsePlanField(field);
  if (!parsed) {
    return undefined;
  }

  return {
    planId: parsed.planId,
    fieldPath: qualifyFieldPath(benefitKey, parsed.network),
  };
};

/**
 * Maps each displayed column to the column that draws its cell on this row. A
 * benefit that doesn't depend on the network is drawn once, by the first
 * network column of its plan, and spans the rest; the cell's identity comes
 * from that first column, as it does for the renderer and the single-cell menu.
 */
const mapColumnsToSpanOwner = (
  columns: Column[],
  node: IRowNode,
): Map<Column, Column> => {
  interface SpanWalk {
    pairs: [Column, Column][];
    openSpan?: { owner: Column; columnsLeft: number };
  }

  const { pairs } = columns.reduce<SpanWalk>(
    ({ pairs: walked, openSpan }, column) =>
      openSpan && openSpan.columnsLeft > 0
        ? {
            pairs: [...walked, [column, openSpan.owner]],
            openSpan: { ...openSpan, columnsLeft: openSpan.columnsLeft - 1 },
          }
        : {
            pairs: [...walked, [column, column]],
            openSpan: {
              owner: column,
              columnsLeft: column.getColSpan(node) - 1,
            },
          },
    { pairs: [] },
  );
  return new Map(pairs);
};

const rangeRowIndexes = ({ startRow, endRow }: CellRange): number[] => {
  const start = startRow?.rowIndex ?? 0;
  const end = endRow?.rowIndex ?? start;
  const first = Math.min(start, end);
  return Array.from(
    { length: Math.max(start, end) - first + 1 },
    (_, offset) => first + offset,
  );
};

const isCellInRange = (
  range: CellRange,
  rowIndex: number,
  column: Column,
): boolean =>
  range.columns.includes(column) && rangeRowIndexes(range).includes(rowIndex);

const resolveRangeTargets = (
  params: GetContextMenuItemsParams,
  ranges: CellRange[],
): MenuCellTarget[] => {
  const displayedColumns = params.api.getAllDisplayedColumns();

  const targets = ranges.flatMap((range) =>
    rangeRowIndexes(range).flatMap((rowIndex) => {
      const node = params.api.getDisplayedRowAtIndex(rowIndex);
      if (!node) return [];

      const spanOwners = mapColumnsToSpanOwner(displayedColumns, node);
      return range.columns.flatMap((column) => {
        const target = resolveCellTarget(
          spanOwners.get(column) ?? column,
          node,
        );
        return target ? [target] : [];
      });
    }),
  );

  const uniqueTargets = new Map(
    targets.map((target) => [`${target.planId}::${target.fieldPath}`, target]),
  );
  return [...uniqueTargets.values()];
};

/**
 * The cells a right-click acts on: every cell of the selected ranges when the
 * click lands inside one of them, otherwise the clicked cell alone. AG Grid
 * keeps the selection on a right-click, so a click outside it would otherwise
 * act on cells the user isn't pointing at.
 */
const resolveMenuTargets = (
  params: GetContextMenuItemsParams,
): MenuCellTarget[] => {
  const { column, node } = params;
  const clickedTarget = resolveCellTarget(column, node);
  if (!clickedTarget) {
    return [];
  }

  const rowIndex = node?.rowIndex;
  const ranges = params.api.getCellRanges() ?? [];
  const isClickInsideSelection =
    column != null &&
    rowIndex != null &&
    ranges.some((range) => isCellInRange(range, rowIndex, column));

  return isClickInsideSelection
    ? resolveRangeTargets(params, ranges)
    : [clickedTarget];
};

/**
 * Builds context menu items for plan editor body cells, resolving the cells'
 * identity from their AG Grid columns and rows before handing off to the
 * shared review menu.
 *
 * The menu covers the selected range only when `buildExtraItems` can act on
 * several cells; without it, it covers the clicked cell alone. The review items
 * open a per-cell dialog, so they show only when the menu covers a single cell.
 */
export const createCommentContextMenuItems = (
  params: GetContextMenuItemsParams,
  callbacks: CellContextMenuCallbacks,
  buildExtraItems?: ExtraMenuItemsBuilder,
): (DefaultMenuItem | MenuItemDef)[] => {
  const clickedTarget = resolveCellTarget(params.column, params.node);
  const targets = buildExtraItems
    ? resolveMenuTargets(params)
    : clickedTarget
      ? [clickedTarget]
      : [];
  if (targets.length === 0) {
    return [];
  }

  const [singleTarget] = targets.length === 1 ? targets : [];
  const reviewItems = singleTarget
    ? createReviewMenuItems(
        singleTarget,
        callbacks.getFieldComment(singleTarget.planId, singleTarget.fieldPath),
        {
          onOpenReview: (target, review) =>
            callbacks.onNeedsReview(target.planId, target.fieldPath, review),
          onClearReview: (target, review) =>
            callbacks.onClearReview(target.planId, review),
        },
      )
    : [];

  return [...reviewItems, ...(buildExtraItems?.(targets) ?? [])];
};
